import { jsx as _jsx } from "react/jsx-runtime";
/**
 * ShellHostContext — exposes the platform shell's `ShellHostProps` to
 * any component in a hosted-app's tree, without prop-drilling.
 *
 * The shell's `AppHost` builds a `ShellHostProps` object per app (see
 * `cloud/dp/shell/src/components/AppHost.tsx`) and passes it to the
 * declarative `App({ shellHost })` entry. Apps wrap their routes with
 * `<ShellHostProvider value={shellHost}>` so deep descendants can read
 * the shell-provided fetch, polarity, orgId, lifecycle, etc.
 *
 * Why a dedicated context:
 *   - The shell's authed `fetch` (`shellHost.fetch`) injects the JWT
 *     bearer header, retries on silent-refresh, and handles 503
 *     maintenance pauses. Hitting `/v1/*` with the global `window.fetch`
 *     skips all of that — the api fixture rejects unauthenticated calls
 *     with 401 "Missing authorization header".
 *   - Apps need polarity / orgId / restoredState / lifecycle in many
 *     places; a context keeps the wiring trivial.
 *   - Multiple apps in the same bundle (test harnesses, dev shells)
 *     can swap the value to inject test fixtures.
 *
 * Strict-mode by default: `useShellHost()` and `useShellFetch()` throw
 * when called outside a provider. A missing wire-up surfaces loudly at
 * mount time instead of silently degrading to a global fetch with no
 * auth.
 *
 * Deprecated path (R-128, R-131). An app that declares only `shell[]`
 * still mounts through the legacy adapter of `@zion/sdk-surfaces-react`,
 * which passes this `shellHost` prop. So every export of this module
 * stays and works, and each one carries `@deprecated` with its
 * `@zion/sdk-surfaces-react/v1` counterpart named. Stage 5b deletes
 * nothing here; a future plan removes them under a major version.
 */
import { createContext, useContext } from 'react';
const ShellHostContext = createContext(null);
/**
 * Wrap a hosted app's route tree with the platform shell's
 * `shellHost` value. The bundle entry typically does:
 *
 * ```tsx
 * export function App({ shellHost }: { shellHost: ShellHostProps }) {
 *   return <ShellHostProvider value={shellHost}><Routes /></ShellHostProvider>;
 * }
 * ```
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Delete the wrapper. `defineSurface`
 * from `@zion/sdk-surfaces-react/v1` supplies the mount's context to every hook under it. The guide
 * is `upgrade-app-to-surfaces`.
 */
export function ShellHostProvider({ value, children, }) {
    return _jsx(ShellHostContext, { value: value, children: children });
}
/**
 * Read the full `ShellHostProps` snapshot. Throws when called outside
 * a `<ShellHostProvider>` so missing wire-ups surface loudly. Most
 * callers want a more specific hook (`useShellFetch`,
 * `useShellPolarity`); use this only when you need multiple fields.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `useServices()` from
 * `@zion/sdk-surfaces-react/v1` and read the member you need. The guide is
 * `upgrade-app-to-surfaces`.
 */
export function useShellHost() {
    const ctx = useContext(ShellHostContext);
    if (!ctx) {
        throw new Error('useShellHost must be called inside <ShellHostProvider>. The bundle entry should wrap its routes with <ShellHostProvider value={shellHost}>.');
    }
    return ctx;
}
/**
 * Read the shell-provided authed fetch. Use this for every `/v1/*`
 * call from inside a hosted app — the global `window.fetch` skips JWT
 * injection and silent refresh, which causes 401s on the api fixture.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `useServices().fetch` from
 * `@zion/sdk-surfaces-react/v1`: the same authed fetch, scoped to the mount. The guide is
 * `upgrade-app-to-surfaces`.
 */
export function useShellFetch() {
    return useShellHost().fetch;
}
/**
 * Read the shell-resolved polarity (`'light'` | `'dark'`).
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `useServices().polarity` from
 * `@zion/sdk-surfaces-react/v1`: `current().resolved` reads it and `subscribe` re-renders on a
 * change. The guide is `upgrade-app-to-surfaces`.
 */
export function useShellPolarity() {
    return useShellHost().polarity;
}
/**
 * Read the active org id, or `null` when no org is selected.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `useServices().session` from
 * `@zion/sdk-surfaces-react/v1`: `current().orgId` is `null` when signed out. The guide is
 * `upgrade-app-to-surfaces`.
 */
export function useShellOrgId() {
    return useShellHost().orgId;
}
/**
 * Read the lifecycle observers (suspend/resume) for the active fixture.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Return a cleanup from `useEffect`
 * inside a `defineSurface` root of `@zion/sdk-surfaces-react/v1`. React runs it in the unmount,
 * while the services still answer. The guide is `upgrade-app-to-surfaces`.
 */
export function useShellLifecycle() {
    return useShellHost().lifecycle;
}
/**
 * Read the persisted state restored on mount, if any.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use
 * `useServices().savedState.restore()` from `@zion/sdk-surfaces-react/v1`; it is `null` on a first
 * mount. The guide is `upgrade-app-to-surfaces`.
 */
export function useShellRestoredState() {
    return useShellHost().restoredState;
}
/**
 * Read the `onSaveState` callback. Apps call this to persist app-level
 * state across suspend/resume — the shell stores it keyed by appId.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `useServices().savedState.save`
 * from `@zion/sdk-surfaces-react/v1` with a plain JSON object. The guide is
 * `upgrade-app-to-surfaces`.
 */
export function useShellSaveState() {
    return useShellHost().onSaveState;
}
/**
 * Read the element portalled UI should mount into, so the shell's scoped
 * CSS applies to it. `null` when the host provides no container.
 *
 * The shell scopes an app's stylesheet to `[data-hosted-app="<appId>"]`
 * and renders the app inside that element. React portals default to
 * `document.body`, which is not a descendant of it, so anything portalled
 * to the default target is matched by none of the app's CSS and renders
 * unstyled (#2252).
 *
 * ```tsx
 * const portalContainer = useShellPortalContainer();
 *
 * // Opened by user action — the common case.
 * <Dialog.Portal container={portalContainer ?? undefined}>
 *
 * // Can be open on the FIRST render (restored state, deep link,
 * // defaultOpen) — gate instead, so it never targets document.body and
 * // never re-parents:
 * {portalContainer && <Dialog.Portal container={portalContainer}>…</Dialog.Portal>}
 * ```
 *
 * ⛔ A subscription, not a one-shot. It is `null` until the shell's
 * callback ref attaches, and may return to `null` on any later render —
 * the shell renders under `<StrictMode>`, which detaches and re-attaches
 * refs in development, and the element unmounts while another app's
 * bundle loads. Never capture it in a `useEffect` with an empty
 * dependency array, and never non-null-assert it.
 *
 * ⚠️ Against a shell older than the release that added this field the
 * value is `undefined` rather than `null`. Both idioms above stay correct.
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `usePortalTarget()` from
 * `@zion/sdk-surfaces-react/v1`; it is never `null`. The guide is `upgrade-app-to-surfaces`.
 */
export function useShellPortalContainer() {
    // Normalised to `null`. A host running a shell older than the release that
    // added the field supplies no value at all, so the raw read is `undefined`
    // there — and the declared type says `HTMLElement | null`. Collapsing it here
    // keeps `portalContainer === null` a reliable "no container" test instead of a
    // false negative that no type error would catch.
    return useShellHost().portalContainer ?? null;
}
/**
 * Read the URL prefix the shell mounted this app under, or `null` when
 * the host supplies no matching active fixture.
 *
 * `mountPath` is load-bearing in three places where a mistake fails
 * silently. Stripping it turns the browser pathname into a path a
 * private in-app router understands; prepending it turns an in-app path
 * into an `href` the shell's router will match; and `restoredState.route`
 * arrives as the full browser pathname with the prefix still on it, so
 * restoring it into a private router needs the same strip. Get any of
 * the three wrong and the app renders, nothing throws, and the browser
 * URL stops describing the screen. The shell keeps reading that URL:
 * `Sidebar.tsx` draws the app's own navigation as
 * `NavLink to={mountPath + item.path}` and takes the active highlight
 * from the browser location, `AppHost.tsx` picks the bundle with
 * `matchAppByPath(registry, location.pathname)`, and `AppHost.tsx`
 * records `FixtureSavedState.route` as `location.pathname`. The mount
 * sits under the `/*` splat, so a URL change inside the mount does not
 * remount the app and a divergence stays.
 *
 * Returns `null` in two cases, and the type forces you to handle both:
 * there is no active fixture, or `activeFixtureId` names a fixture the
 * host did not include in `fixtures`. Neither occurs under the platform
 * shell today, which builds the whole props object only once an app is
 * active. The type follows the contract instead: `activeFixtureId` is
 * typed `string | null` and `fixtures` carries no guarantee of holding
 * it, so a `string` return would need an assertion this repo bans or a
 * throw inside a hosted app. Concatenating the `null` produces the
 * string `"null/notes"`, which routes nowhere and throws nothing, so
 * branch on it rather than coercing it.
 *
 * ```tsx
 * const mountPath = useShellMountPath();
 * // `inAppPath` is named separately on purpose: an example that prefixes the
 * // mount's own segment reads as `/notes` + `/notes` for an app mounted at
 * // `/notes`, which is the one shape a reader should not copy.
 * const inAppPath = '/inbox';
 * const href = mountPath === null ? undefined : `${mountPath}${inAppPath}`;
 * ```
 *
 * @deprecated Since layered-shell-surfaces stage 5b (R-131). Use `useServices().location` from
 * `@zion/sdk-surfaces-react/v1`: `current().mountPath` is the prefix, or `null` for an embedded
 * mount. The guide is `upgrade-app-to-surfaces`.
 */
export function useShellMountPath() {
    const { fixtures, activeFixtureId } = useShellHost();
    const active = fixtures.find((f) => f.fixtureId === activeFixtureId);
    return active?.mountPath ?? null;
}
//# sourceMappingURL=ShellHostContext.js.map